<template>
  <div :class="['control is-clearfix mde', containerClass]">
    <markdown-editor
      ref="mde"
      :configs="configs"
      preview-class="markdown-body"
      :value="value"
      @input="$emit('input', $event)"
    />
    <mc-image-select-button
      ref="imageButton"
      type="OTHER"
      :programmatic="true"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import MarkdownEditor from 'vue-simplemde/src/markdown-editor';
import { ImageSelect } from './image';
import { parsedGcsUrlInMarkdown } from '../../utils/url';
import { getEmoji } from '../../ui/modals/EmojiSelectorModal';
import { getSymbol } from '../../ui/modals/SymbolSelectorModal';
const storageBucket = process.env['VUE_APP_FIREBASE_STORAGE_BUCKET'];

const selectionReplacer = (editor, text) => {
  const cm = editor.codemirror;

  if (/editor-preview-active/.test(cm.getWrapperElement().lastChild.className))
    return;

  const startPoint = cm.getCursor('start');
  const endPoint = cm.getCursor('end');

  // TODO Use cm.getTokenAt(startPint) and replace if the position is alreadya image link.
  // and support replacement of selected text.

  if (text) {
    cm.replaceSelection(text);
    if (startPoint.ch != endPoint.ch && startPoint.line != endPoint.line) {
      endPoint.line = startPoint.line;
      endPoint.ch = startPoint.ch + text.length;
    } else {
      startPoint.ch += text.length;
      endPoint.ch += text.length;
    }
  }
  cm.setSelection(startPoint, endPoint);
  cm.focus();
};

export default {
  components: {
    MarkdownEditor
  },
  props: {
    containerClass: {
      type: String,
      default: ''
    },
    value: {
      type: String,
      default: ''
    }
  },
  data: function() {
    return {
      configs: {
        spellChecker: false,
        status: false,
        previewRender: markdownText => {
          const mde = this.$refs.mde.simplemde;
          const urlReplacedText = parsedGcsUrlInMarkdown(markdownText);
          return mde.markdown(urlReplacedText);
        },
        toolbar: [
          'heading-1',
          'heading-2',
          'heading-3',
          '|',
          'bold',
          'italic',
          '|',
          'quote',
          'code',
          '|',
          'unordered-list',
          'ordered-list',
          '|',
          'link',
          {
            name: 'image',
            action: async editor => {
              const url = await this.getImageLink();
              if (url) selectionReplacer(editor, `![image](${url})`);
            },
            className: 'fa fa-picture-o',
            title: 'Insert Image'
          },
          '|',
          {
            name: 'emoji',
            action: async editor => {
              try {
                selectionReplacer(editor, await getEmoji(this));
              } catch {
                return;
              }
            },
            className: 'fa fa-smile-o',
            title: 'Insert Emoji'
          },
          {
            name: 'symbol',
            action: async editor => {
              try {
                selectionReplacer(editor, await getSymbol(this));
              } catch {
                return;
              }
            },
            className: 'fa fa-inr',
            title: 'Insert symbol'
          },
          '|',
          'preview'
        ]
      }
    };
  },
  methods: {
    refresh() {
      /**
       * A bug with simple mde causes `markdown-editor` to not load its initial
       * value untill it is focused when placed inside elements which render
       * it conditionally like `modals`, `b-collapse`. When using
       * `markdown-editor` in such components make sure to call this method
       * every time that parent component is rendered/shown.
       *
       * Also we call refresh in nextTick as `refresh` function of code mirror
       * is available only in the next tick after it is rendered.
       */
      this.$nextTick(function() {
        this.$refs.mde.simplemde.codemirror.refresh();
      }, this);
    },
    getImageLink() {
      return new Promise(resolve => {
        const imageButton = this.$refs.imageButton;
        const handler = url => {
          imageButton.$off('input', handler);
          imageButton.$off('close', handler);
          resolve(url);
        };
        imageButton.$on('input', handler);
        imageButton.$on('close', handler);
        imageButton.openGallary();
      });
    }
  }
};
</script>

<style lang="scss">
@import '~simplemde/dist/simplemde.min.css';
@import '~github-markdown-css';

.markdown-editor .CodeMirror,
.markdown-editor .CodeMirror-scroll {
  min-height: $mde-min-height;
}
</style>
